import * as React from "react";

/**
 * Combine multiple refs (callback or object) into one stable ref callback.
 *
 * @example
 * // Forward a single ref to both a local measurement ref and a forwarded ref
 * const MyInput = React.forwardRef((props, forwardedRef) => {
 *   const measureRef = React.useRef(null);
 *   return <input {...props} ref={mergeRefs(measureRef, forwardedRef)} />;
 * });
 *
 * @example
 * // Use with popper/anchor refs where both library and consumer need the node
 * const anchorRef = React.createRef();
 * const forkedRef = mergeRefs(anchorRef, externalRef);
 */
export function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(node);
      } else {
        ref.current = node;
      }
    });
  };
}

/**
 * Memoized version of mergeRefs for use inside components/hooks.
 *
 * @example
 * const anchorRef = React.useCallback(node => setAnchorEl(node), []);
 * const mergedRef = useMergedRefs(anchorRef, inputRefFromHook, forwardedRef);
 * return <input ref={mergedRef} />;
 */
export function useMergedRefs(...refs) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => mergeRefs(...refs), refs);
}
